import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { motion } from "motion/react";
import { Floating, type FloatingAlign } from "./floating";
import { useFieldControl, type InputSize } from "./controls";
import { Icon, type IconName } from "./icons";
import { IconButton } from "./index";

/* ---------------------------------------------------------------- select */

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** Quieter text under or beside the label, like a client name. */
  hint?: string | undefined;
  icon?: IconName | undefined;
  /** A colour square in place of an icon, like a Project's colour. Any CSS colour. */
  swatch?: string | undefined;
  /** Draws a rule above this option, to set apart actions like "New client…". */
  divider?: boolean | undefined;
}

const TYPEAHEAD_RESET = 650;

/** Index of the next option whose label starts with `query`, searching from after `from`. */
function typeaheadMatch<T extends string>(options: Array<SelectOption<T>>, query: string, from: number): number {
  const needle = query.toLowerCase();
  for (let step = 1; step <= options.length; step++) {
    const index = (from + step) % options.length;
    if (options[index]!.label.toLowerCase().startsWith(needle)) return index;
  }
  return -1;
}

/**
 * A listbox in place of a native select: a quiet trigger, a floating list with a sliding highlight,
 * full keyboard support (arrows, Home/End, type to jump), and an optional filter for long lists.
 */
export function Select<T extends string>({ value, options, onChange, placeholder = "Choose…", searchable = false, searchPlaceholder = "Filter…", size = "md", variant = "field", disabled = false, fitWidth = true, className = "", id, "aria-label": ariaLabel }: {
  value: T;
  options: Array<SelectOption<T>>;
  onChange: (value: T) => void;
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  size?: InputSize;
  variant?: "field" | "ghost";
  disabled?: boolean;
  /** Keep the list as wide as the trigger (the default); false lets it grow to fit long options. */
  fitWidth?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}) {
  const control = useFieldControl(id);
  const listId = useId();
  const highlightId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const typeahead = useRef({ query: "", at: 0 });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const visible = searchable && query.trim()
    ? options.filter((option) => `${option.label} ${option.hint ?? ""}`.toLowerCase().includes(query.trim().toLowerCase()))
    : options;
  const selected = options.find((option) => option.value === value);

  const show = (initial?: number) => {
    if (disabled) return;
    const index = options.findIndex((option) => option.value === value);
    setQuery("");
    setActive(initial ?? Math.max(0, index));
    setOpen(true);
  };

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus({ preventScroll: true });
  }, []);

  const dismiss = useCallback(() => close(false), [close]);

  const choose = (option: SelectOption<T> | undefined) => {
    if (!option) return;
    close();
    if (option.value !== value) onChange(option.value);
  };

  useEffect(() => {
    if (open) (searchable ? searchRef.current : listRef.current)?.focus({ preventScroll: true });
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);

  const onListKey = (event: KeyboardEvent) => {
    const last = visible.length - 1;
    switch (event.key) {
      case "ArrowDown": event.preventDefault(); setActive((index) => Math.min(last, index + 1)); return;
      case "ArrowUp": event.preventDefault(); setActive((index) => Math.max(0, index - 1)); return;
      case "Home": event.preventDefault(); setActive(0); return;
      case "End": event.preventDefault(); setActive(last); return;
      case "Enter": event.preventDefault(); choose(visible[active]); return;
      case "Escape": event.preventDefault(); event.stopPropagation(); close(); return;
      case "Tab": event.preventDefault(); close(); return;
      case " ":
        if (searchable) return;
        event.preventDefault();
        choose(visible[active]);
        return;
    }
    if (!searchable && event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const now = Date.now();
      const state = typeahead.current;
      state.query = now - state.at > TYPEAHEAD_RESET ? event.key : state.query + event.key;
      state.at = now;
      const match = typeaheadMatch(visible, state.query, state.query.length > 1 ? active - 1 : active);
      if (match >= 0) setActive(match);
    }
  };

  const onTriggerKey = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      show();
    }
  };

  const activeOption = visible[active];

  return <>
    <button
      ref={triggerRef}
      type="button"
      id={control.id}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-label={ariaLabel}
      aria-describedby={control["aria-describedby"]}
      aria-invalid={control["aria-invalid"]}
      disabled={disabled}
      className={`ot-select ot-select--${size} ot-select--${variant} ${open ? "is-open" : ""} ${className}`.trim()}
      onClick={() => (open ? close() : show())}
      onKeyDown={onTriggerKey}
    >
      {selected?.swatch ? <i className="ot-swatch ot-select__icon" style={{ background: selected.swatch }} aria-hidden="true"/> : selected?.icon && <Icon name={selected.icon} className="ot-select__icon"/>}
      <span className={`ot-select__value ${selected ? "" : "is-placeholder"}`}>
        <span className="ot-select__label">{selected ? selected.label : placeholder}</span>
        {selected?.hint && <span className="ot-select__hint">{selected.hint}</span>}
      </span>
      <Icon name="caret-down" weight="bold" className="ot-select__caret"/>
    </button>
    <Floating open={open} anchorRef={triggerRef} floatingRef={floatingRef} onDismiss={dismiss} matchWidth fitWidth={fitWidth} className="ot-listbox-panel">
      {searchable && <div className="ot-listbox__search">
        <Icon name="search"/>
        <input
          ref={searchRef}
          value={query}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          aria-controls={listId}
          aria-activedescendant={activeOption ? `${listId}-${active}` : undefined}
          onChange={(event) => { setQuery(event.target.value); setActive(0); }}
          onKeyDown={onListKey}
        />
      </div>}
      <ul
        ref={listRef}
        id={listId}
        role="listbox"
        tabIndex={-1}
        className="ot-listbox"
        aria-label={ariaLabel ?? selected?.label}
        aria-activedescendant={activeOption ? `${listId}-${active}` : undefined}
        onKeyDown={onListKey}
      >
        {visible.length === 0 && <li className="ot-listbox__empty" role="presentation">Nothing matches</li>}
        {visible.map((option, index) => {
          const isSelected = option.value === value;
          const isActive = index === active;
          return <li
            key={option.value}
            id={`${listId}-${index}`}
            data-index={index}
            role="option"
            aria-selected={isSelected}
            className={`ot-listbox__option ${isActive ? "is-active" : ""} ${isSelected ? "is-selected" : ""} ${option.divider ? "has-divider" : ""}`}
            onPointerMove={() => { if (!isActive) setActive(index); }}
            onClick={() => choose(option)}
          >
            {isActive && <motion.span layoutId={highlightId} className="ot-listbox__highlight" transition={{ type: "spring", stiffness: 700, damping: 46 }}/>}
            {option.swatch ? <i className="ot-swatch ot-listbox__icon" style={{ background: option.swatch }} aria-hidden="true"/> : option.icon && <Icon name={option.icon} className="ot-listbox__icon"/>}
            <span className="ot-listbox__text">
              <span>{option.label}</span>
              {option.hint && <span className="ot-listbox__hint">{option.hint}</span>}
            </span>
            {isSelected && <Icon name="check" weight="bold" className="ot-listbox__check"/>}
          </li>;
        })}
      </ul>
    </Floating>
  </>;
}

/* ---------------------------------------------------------------- menu */

export interface MenuItem {
  label: string;
  icon?: IconName;
  hint?: string;
  tone?: "default" | "danger";
  disabled?: boolean;
  onSelect: () => void;
}

/** A "more actions" button that opens a short floating menu. */
export function Menu({ label, items, icon = "dots", align = "end", className = "", trigger }: {
  label: string;
  items: MenuItem[];
  icon?: IconName;
  align?: FloatingAlign;
  className?: string;
  /** Visible text for a labelled trigger instead of an icon button. */
  trigger?: ReactNode;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();
  const highlightId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const enabled = items.map((item, index) => (item.disabled ? -1 : index)).filter((index) => index >= 0);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus({ preventScroll: true });
  }, []);
  const dismiss = useCallback(() => close(false), [close]);

  useEffect(() => {
    if (open) itemRefs.current[active]?.focus({ preventScroll: true });
  }, [open, active]);

  const step = (delta: number) => {
    const position = enabled.indexOf(active);
    const next = enabled[(position + delta + enabled.length) % enabled.length];
    if (next !== undefined) setActive(next);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown": event.preventDefault(); step(1); break;
      case "ArrowUp": event.preventDefault(); step(-1); break;
      case "Home": event.preventDefault(); setActive(enabled[0] ?? 0); break;
      case "End": event.preventDefault(); setActive(enabled[enabled.length - 1] ?? 0); break;
      case "Escape": event.preventDefault(); event.stopPropagation(); close(); break;
      case "Tab": event.preventDefault(); close(); break;
    }
  };

  const openMenu = () => {
    setActive(enabled[0] ?? 0);
    setOpen(true);
  };

  const triggerProps = {
    ref: triggerRef,
    "aria-haspopup": "menu" as const,
    "aria-expanded": open,
    "aria-controls": open ? menuId : undefined,
    onClick: () => (open ? close() : openMenu()),
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        openMenu();
      }
    }
  };

  return <>
    {trigger
      ? <button type="button" className={`ot-button ot-button--default ${className}`.trim()} {...triggerProps}>{trigger}<Icon name="caret-down" weight="bold" className="ot-select__caret"/></button>
      : <IconButton label={label} icon={icon} className={`is-ghost ${open ? "is-pressed" : ""} ${className}`.trim()} {...triggerProps}/>}
    <Floating open={open} anchorRef={triggerRef} floatingRef={floatingRef} onDismiss={dismiss} align={align} className="ot-menu-panel">
      <div role="menu" id={menuId} aria-label={label} className="ot-menu" onKeyDown={onKeyDown}>
        {items.map((item, index) => <button
          key={item.label}
          ref={(node) => { itemRefs.current[index] = node; }}
          type="button"
          role="menuitem"
          tabIndex={index === active ? 0 : -1}
          disabled={item.disabled}
          className={`ot-menu__item ${item.tone === "danger" ? "is-danger" : ""}`}
          onPointerMove={() => { if (!item.disabled && index !== active) setActive(index); }}
          onClick={() => {
            close();
            item.onSelect();
          }}
        >
          {index === active && <motion.span layoutId={highlightId} className="ot-listbox__highlight" transition={{ type: "spring", stiffness: 700, damping: 46 }}/>}
          {item.icon && <Icon name={item.icon} className="ot-menu__icon"/>}
          <span className="ot-menu__label">{item.label}</span>
          {item.hint && <span className="ot-menu__hint">{item.hint}</span>}
        </button>)}
      </div>
    </Floating>
  </>;
}
